import { useCallback, useState } from 'react';
import { Select } from '@rio-cloud/rio-uikit';
import { useIntl } from 'react-intl';

import { fetchBudgetServicesV2, fetchBudgetServiceItemsV2 } from '../../../../../api/fetchBudgetV2';

import { IBudgetVersionService, IBudget, IBudgetServiceItem, IBudgetVersionServiceItem } from '../../../../../models/budgetV2';

import SearchAutocomplete from '../../../../form/searchAutocomplete';

import { BudgetServiceType } from '../../../enums/budgetService';

import ErrorDialog from '../../../../dialogs/ErrorDialog';

interface IBudgetServiceSelectsProps {
  serviceIndex: number;
  budget: IBudget
  hasTruckerPayPlan: boolean;
  loadingServiceItems: boolean;

  setService: (service: IBudgetVersionService) => void;
  setLoadingServiceItems: (loading: boolean) => void;
  setServiceItems: (items: IBudgetVersionServiceItem[]) => void;
}

export const mapCatalogItemToVersionServiceItem = (item: IBudgetServiceItem): IBudgetVersionServiceItem => ({
  id: item.id,
  ref: item.ref,
  item: item.item,
  description: item.description,
  quantity: item.qtd ?? 0,
  value: item.value ?? 0,
  discount: item.discount ?? 0,
  subtotal: item.subtotal ?? 0,
  isCovered: item.isCovered,
  notCoveredReason: item.notCoveredReason,
  availableIn: item.availableIn,
});

export const BudgetServiceChooser = (props: IBudgetServiceSelectsProps) => {
  const intl = useIntl();

  const { 
    budget, serviceIndex, hasTruckerPayPlan, loadingServiceItems, 
    setService, setLoadingServiceItems, setServiceItems 
  } = props;
  
  const service = budget.versionData?.services?.[serviceIndex];
  if (!service) return null;
    
  const [error, setError] = useState<{title: string; body: string} | null>(null);

  const effectiveServiceType = service.serviceType === BudgetServiceType.CORRECTIVE ? BudgetServiceType.PLAN : service.serviceType as BudgetServiceType;

  const loadServiceData = useCallback(
    async (
      searchTerm: string
    ): Promise<{ id: string; label: string; sku: string }[]> => {
      const chassis = budget.vehicleChassis ?? '';
      const odometer = budget.vehicleOdometer?.toString();
      const hourMeter = budget.vehicleHourMeter?.toString();
      const services = await fetchBudgetServicesV2(effectiveServiceType, chassis, searchTerm, 0, 20, odometer, hourMeter);
      return services.map((service) => ({
        id: service.id ?? '',
        label: service.name ?? '',
        sku: service.sku ?? '',
      }));
    },
    [effectiveServiceType]
  );

  const handlePackageSelected = async (option: {id: string; label: string, sku: string}) => {
    if (props.budget.versionData?.services?.some((item) => item.id === option.id)) {
      setError({title: intl.formatMessage({ id: 'intl-msg:budgets.serviceChooser.error.selectPackage' }), body: intl.formatMessage({ id: 'intl-msg:budgets.serviceChooser.error.packageAlreadyAdded' })});
      return;
    }

    setLoadingServiceItems(true);
    try {
      const items = await fetchBudgetServiceItemsV2(
        { serviceType: service.serviceType as BudgetServiceType, serviceId: option.id, chassis: budget.vehicleChassis ?? '', odometer: budget.vehicleOdometer?.toString(), hourMeter: budget.vehicleHourMeter?.toString() },
      );
      if (!items.length) {
        setError({title: intl.formatMessage({ id: 'intl-msg:budgets.serviceChooser.error.selectPackage' }), body: intl.formatMessage({ id: 'intl-msg:budgets.serviceChooser.error.packageNotLoaded' })});
        setLoadingServiceItems(false);
        return;
      }

      // `ref` is the row sequence (e.g. 2.01), not the catalog item reference
      const versionServiceItems = items.map((item, index) => ({
        ...mapCatalogItemToVersionServiceItem(item),
        ref: `${props.serviceIndex + 1}.${String(index + 1).padStart(2, '0')}`,
      }));

      setServiceItems(versionServiceItems);
      setService({
        ...service,
        id: option.id,
        ref: option.sku,
        code: option.sku,
        name: option.label,
        serviceItems: versionServiceItems,
        items: versionServiceItems,
      });
    } finally {
      setLoadingServiceItems(false);
    }
  };

  return (
    <div className="flex flex-row gap-10 flex-1 w-full">
      <div className="w-[150px]">
        <div className="font-bold mb-2 text-size-12 text-gray-500 whitespace-nowrap">
          {intl.formatMessage({ id: 'intl-msg:budgets.serviceChooser.serviceType' })}
        </div>
        <Select
          options={[
            { id: 'PREVENTIVE', label: intl.formatMessage({ id: 'intl-msg:budgets.serviceChooser.preventive' }) },
            ...(hasTruckerPayPlan ? [{ id: 'PLAN', label: 'Sob Medida' }] : []),
            { id: 'OTHER', label: intl.formatMessage({ id: 'intl-msg:budgets.serviceChooser.otherServices' }) },
          ]}
          placeholder={intl.formatMessage({ id: 'intl-msg:budgets.serviceChooser.serviceType' })}
          value={[
            service.serviceType === BudgetServiceType.PLAN && !hasTruckerPayPlan ? BudgetServiceType.PREVENTIVE : effectiveServiceType 
              ?? '',
          ]}
          onChange={(option) => {
            setService({
              ...service,
              serviceType: option?.id as BudgetServiceType,
            });
          }}
        />
      </div>

      {effectiveServiceType === BudgetServiceType.PREVENTIVE || effectiveServiceType === BudgetServiceType.PLAN ? (
        <div className="w-[300px]">
          <div className="font-bold mb-2 text-size-12 text-gray-500 whitespace-nowrap">
            {intl.formatMessage({ id: 'intl-msg:budgets.serviceChooser.packageServices' })} *
          </div>
          <SearchAutocomplete
            key={service.id}
            id="service-select"
            placeholder={intl.formatMessage({ id: 'intl-msg:budgets.serviceChooser.selectPackage' })}
            fetchOptions={loadServiceData}
            fetchDefaultOptionsOnEmptySearch
            optionLabel={{ label: intl.formatMessage({ id: 'intl-msg:budgets.serviceChooser.name' }) }}
            optionRenderer={({ label }) => <div>{label}</div>}
            onSelectedOption={handlePackageSelected}
            overlay={true}
            debounceDelay={300}
            disabled={loadingServiceItems}
            defaultValue={
              service.id && service.name
                ? {
                    id: service.id,
                    label: service.name,
                  }
                : undefined
            }
            valueRenderer={(option) =>
              option?.label || intl.formatMessage({ id: 'intl-msg:budgets.serviceChooser.selectPackage' })
            }
            onClear={() => {
              setService({...service, id: '', ref: '', code: ''});
              setServiceItems([]);
            }}
          />
        </div>
      ) : null}

      <ErrorDialog
        show={!!error}
        onClose={() => setError(null)}
        title={error?.title ?? ''}
        body={error?.body ?? ''}
      />
    </div>
  );
};